import { captureException } from '@sentry/nextjs'

import { PostTestStatus } from 'types/microlearning'
import { JenjangValid } from 'api/microlearning/types'

import type { TType } from 'utils/canvasToBlob'
import type {
  MateriContent,
  ModuleProgress,
  OverallProgress,
  ResponseQuiz,
  TTopicList,
} from 'types/microlearning'

export const isActivityComplete: (
  contents: MateriContent[],
  quiz: ResponseQuiz | boolean
) => boolean = (contents, quiz) => {
  let isComplete = true
  contents.forEach((content) => {
    if (content.completed === false) {
      isComplete = false
    }
  })

  if (typeof quiz === 'boolean') {
    return isComplete && quiz
  }

  return isComplete && quiz.completed
}

export const canDoAksiNyata: (modules: ModuleProgress[]) => boolean = (
  modules
) => {
  return (
    modules.length > 0 &&
    !modules.some((module) => {
      return module.postTest !== 'passed'
    })
  )
}

export type TopicProgressState =
  | 'DEFAULT'
  | 'MODULE_ON_PROGRESS'
  | 'MODULE_DONE'
  | 'AKSI_NYATA_CREATED'
  | 'AKSI_NYATA_ON_PROGRESS'
  | 'AKSI_NYATA_DONE'
  | 'AKSI_NYATA_VALIDATED'
  | 'AKSI_NYATA_NEED_REVISION'
  | 'AKSI_NYATA_APPEAL'

export function determineTopicState(
  progress: OverallProgress
): TopicProgressState {
  if (progress?.modules?.length > 0) {
    const untouchModules = progress?.modules?.filter(
      (module) => !module.material
    )

    const halfFinishedModule = progress?.modules?.filter((module) => {
      const isPostTestNotPassed =
        typeof module.postTest === 'boolean'
          ? !module.postTest
          : module.postTest !== PostTestStatus.Passed
      return !!module.material && isPostTestNotPassed
    })

    const finishedModules = progress?.modules?.filter((module) => {
      return typeof module.postTest === 'boolean'
        ? !!module.postTest
        : module.postTest === PostTestStatus.Passed
    })

    if (untouchModules.length === progress?.modules?.length) {
      return 'DEFAULT'
    }

    if (
      (halfFinishedModule.length > 0 || finishedModules.length > 0) &&
      finishedModules.length < progress?.modules?.length
    ) {
      return 'MODULE_ON_PROGRESS'
    }

    if (
      finishedModules.length === progress?.modules?.length &&
      progress?.aksiNyata?.state === 'NOT_CREATED'
    ) {
      return 'MODULE_DONE'
    }

    if (progress?.aksiNyata?.state === 'CREATED') {
      return 'AKSI_NYATA_CREATED'
    }

    if (progress?.aksiNyata?.state === 'CONFIRMED') {
      return 'AKSI_NYATA_ON_PROGRESS'
    }

    if (progress?.aksiNyata?.state === 'NEED_REVISION') {
      return 'AKSI_NYATA_NEED_REVISION'
    }

    if (progress?.aksiNyata?.state === 'APPEAL') {
      return 'AKSI_NYATA_APPEAL'
    }

    if (progress?.aksiNyata?.state === 'COMPLETED') {
      return 'AKSI_NYATA_DONE'
    }

    if (progress?.aksiNyata?.state === 'VALIDATED') {
      return 'AKSI_NYATA_VALIDATED'
    }
  }

  return 'DEFAULT'
}

export function determineTopicStateByList(topic: TTopicList) {
  const untouchedModules = topic.percentage === 0
  const halfFinishedModules = topic.percentage > 0 && topic.percentage < 100
  const finishedModules = topic.percentage === 100

  if (untouchedModules) {
    return 'DEFAULT'
  }

  if (halfFinishedModules) {
    return 'MODULE_ON_PROGRESS'
  }

  if (finishedModules && topic.aksiNyataState === 'NOT_CREATED') {
    return 'MODULE_DONE'
  }

  if (topic.aksiNyataState === 'CREATED') {
    return 'AKSI_NYATA_CREATED'
  }

  if (topic.aksiNyataState === 'CONFIRMED') {
    return 'AKSI_NYATA_ON_PROGRESS'
  }

  if (topic.aksiNyataState === 'NEED_REVISION') {
    return 'AKSI_NYATA_NEED_REVISION'
  }

  if (topic.aksiNyataState === 'APPEAL') {
    return 'AKSI_NYATA_APPEAL'
  }

  if (topic.aksiNyataState === 'COMPLETED') {
    return 'AKSI_NYATA_DONE'
  }

  if (topic.aksiNyataState === 'VALIDATED') {
    return 'AKSI_NYATA_VALIDATED'
  }

  return 'DEFAULT'
}

export function determineBadge(topic: TTopicList) {
  const topicProgressState = determineTopicStateByList(topic)

  switch (topicProgressState) {
    default:
      return undefined
    case 'MODULE_ON_PROGRESS':
      return {
        type: 'informational',
        text: 'Pelajari Modul',
      }
    case 'MODULE_DONE':
    case 'AKSI_NYATA_CREATED':
    case 'AKSI_NYATA_ON_PROGRESS':
    case 'AKSI_NYATA_NEED_REVISION':
    case 'AKSI_NYATA_DONE':
    case 'AKSI_NYATA_APPEAL':
      return {
        type: 'informational',
        text: 'Surat selesai topik tersedia',
        state: 'ongoing',
      }
    case 'AKSI_NYATA_VALIDATED':
      if (!topic.certifiable) {
        return {
          type: 'neutral',
          text: 'Menunggu hasil validasi',
          state: 'completed',
        }
      }
      return {
        type: 'success',
        text: 'Sertifikat tersedia',
        state: 'validated',
      }
  }
}

type TDownloadImageFromUrlParam = {
  url: string
  mime: TType
  filename?: string
  onError?: (error: Error) => void
  onSuccess?: () => void
  asNewTab?: boolean
}
export const downloadImageFromUrl = async ({
  url,
  mime,
  filename = '',
  onError,
  onSuccess,
  asNewTab = true,
}: TDownloadImageFromUrlParam) => {
  const anchor = document.createElement('a')

  if (asNewTab) {
    anchor.setAttribute('href', url)
    anchor.setAttribute('download', `${filename}.${mime}`)
    anchor.setAttribute('target', '_blank')
    anchor.click()
    onSuccess?.()
    return
  }

  try {
    const response = await fetch(url)
    const blob = await response.blob()
    const objURL = URL.createObjectURL(blob)
    anchor.setAttribute('href', objURL)
    anchor.setAttribute('download', `${filename}.${mime}`)
    anchor.click()
    onSuccess?.()
  } catch (error) {
    onError?.(error)
    captureException(error)
  }
}

export const getCertificateAssets = () => {
  if (process.env.NEXT_PUBLIC_ENV !== 'production') {
    return {
      background:
        'https://storage.googleapis.com/microlearning-public-staging/certificate-front-page/background.jpg',
      logo: 'https://storage.googleapis.com/microlearning-public-staging/certificate-front-page/logo.png',
    }
  }

  return {
    background:
      'https://storage.googleapis.com/microlearning-public-production/certificate-front-page/background.jpg',
    logo: 'https://storage.googleapis.com/microlearning-public-production/certificate-front-page/logo.png',
  }
}

const VALID_JENJANG: JenjangValid[] = [
  JenjangValid.PAUD,
  JenjangValid.SD,
  JenjangValid.SMP,
  JenjangValid.SMA,
  JenjangValid.SMK,
  JenjangValid.SLB,
]

export const determineJenjangByEmail = (email: string): JenjangValid => {
  const subdomain = email.split('@')[1] // ['putra.prawira', 'admin.sma.belajar.id'], results in 'admin.sma.belajar.id'
  if (!Boolean(subdomain)) return JenjangValid.NONJENJANG

  const subdomainItems = subdomain.split('.') as JenjangValid[] // ['admin', 'sma', 'belajar', 'id']

  return (
    subdomainItems.find((item) => VALID_JENJANG.includes(item)) ??
    JenjangValid.NONJENJANG
  ) // 'sma'
}

export const shouldHideNav = () => {
  if (typeof window === 'undefined') return false
  const query = location?.search
  return query.includes('hide_nav=true')
}

export const cacheImage = (url: string) => {
  const listStorage = [
    'https://storage.googleapis.com/microlearning-public-staging',
    'https://storage.googleapis.com/microlearning-public-production',
  ]

  const newUrl = url.replace(
    listStorage.find((storage) => url.includes(storage)),
    process.env.NEXT_PUBLIC_BELAJAR_CDN
  )

  return newUrl
}
